import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, cn, Dialog, Input, Textarea, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CutJobClient,
  type CutJobDetailDTO,
  type CutJobDTO,
  type CutJobGroupLotDTO,
  type CutJobPanelGroupDTO,
  type PanelRef,
} from "../../../api/cutJobs";
import type { ProductionOrderOperationDTO } from "../../../api/floor";
import { formatNumber, refusalMessage } from "../../../api/manufacturing";
import { NexpimView } from "../../../views";
import { CandidateTable } from "./CandidateTable";
import { CutJobDocuments } from "./CutJobDocuments";
import { CUT_JOBS_KEY, cutJobStatusLabel, cutJobStatusVariant, cutJobTitle, jobProcessLabel } from "./cutJobModel";

export const cutJobKey = (id: number) => [...CUT_JOBS_KEY, "detail", id];

/** "3 × 8862-590 + 2 × 8862-480" */
function panelsText(panels: Array<{ number: string; planned: number }>): string {
  return panels.filter((panel) => Number(panel.planned) > 0).map((panel) => `${formatNumber(panel.planned)} × ${panel.number}`).join(" + ");
}

/**
 * One cut job (docs/cutting-jobs §5, §6), read as the saw cuts it: one card
 * per material (PARTICULE 16 MM NOIR) with its raw panels in total, and under
 * it the lots that share it, each with what it plans of it, what was used of
 * it and its pieces cut, each a row below the lot (all shown; a lot folds). A draft is put together
 * here (Add lots, take a lot out) and confirmed to reach the saw; a confirmed
 * job goes back to a draft, or is cancelled, while nothing was reported on it.
 * When it is due is its lots'; its place at the saw is set on the job list.
 */
export function CutJobDetailPage({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const allowed = hasView(NexpimView.PRODUCTION_ORDERS);
  const detail = useQuery({ queryKey: cutJobKey(jobId), queryFn: () => client.detail(jobId), enabled: allowed });
  const [adding, setAdding] = useState(false);

  const settle = (next: CutJobDetailDTO) => {
    queryClient.setQueryData(cutJobKey(jobId), next);
    void queryClient.invalidateQueries({ queryKey: ["production"] });
  };
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
  const act = useMutation({
    mutationFn: (action: "confirm" | "unconfirm" | "cancel") => client[action](jobId),
    onSuccess: settle,
    onError: fail,
  });
  const removePanel = useMutation({ mutationFn: (ref: PanelRef) => client.removePanels(jobId, [ref]), onSuccess: settle, onError: fail });
  const remove = useMutation({
    mutationFn: () => client.remove(jobId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      void navigate({ to: "/production/cut-jobs" });
    },
    onError: fail,
  });

  const parent = { to: "/production/cut-jobs", label: t("navigation.item.cut-jobs", "Cut jobs") };
  if (!allowed || !detail.data) {
    return (
      <>
        <PageHeader parent={parent} title={t("navigation.item.cut-jobs", "Cut jobs")} />
        <p className="p-4 text-[13px] text-ink-muted lg:p-6">
          {!allowed
            ? t("error.access-denied.detail", "Your account does not have permission to view this page.")
            : detail.isError ? t("cut-job.not-found", "That cut job could not be loaded.") : t("table.loading", "Loading")}
        </p>
      </>
    );
  }
  const { job, lots, panels, pieces, piece_panels: piecePanels, documents } = detail.data;
  const draft = job.status === "DRAFT";
  const open = job.status !== "DONE" && job.status !== "CANCELLED";
  const busy = act.isPending || removePanel.isPending || remove.isPending;
  const ask = (title: string, message: string, label: string) => confirm({ title, message, confirmLabel: label, variant: "danger" });

  return (
    <>
      <PageHeader
        parent={parent}
        title={cutJobTitle(job, t)}
        subtitle={t("cut-job.summary", "{{process}} · {{lots}} lots · {{done}}/{{required}} pieces cut", {
          process: jobProcessLabel(job.process_code ?? "CUT", t),
          lots: lots.length,
          done: formatNumber(job.pieces_done ?? 0),
          required: formatNumber(job.pieces_required ?? 0),
        })}
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              {draft && (
                <Button variant="secondary" icon={<Plus className="size-4" />} disabled={busy} onClick={() => setAdding(true)}>
                  {t("cut-job.add-lots", "Add lots")}
                </Button>
              )}
              {draft && (
                <Button variant="primary" loading={act.isPending} disabled={busy || lots.length === 0} onClick={() => act.mutate("confirm")}>
                  {t("cut-job.confirm", "Confirm the job")}
                </Button>
              )}
              {job.status === "CONFIRMED" && (
                <Button variant="secondary" disabled={busy} onClick={() => act.mutate("unconfirm")}>
                  {t("cut-job.unconfirm", "Back to draft")}
                </Button>
              )}
              {(draft || job.status === "CONFIRMED") && (
                <Button variant="ghost" disabled={busy} onClick={() => {
                  void (async () => {
                    if (await ask(
                      t("cut-job.cancel.title", "Cancel cut job {{n}}?", { n: job.number }),
                      t("cut-job.cancel.message", "Its lots' panels are free for another job. A lot already released needs a new confirmed job for them."),
                      t("cut-job.cancel.confirm", "Cancel the job"),
                    )) act.mutate("cancel");
                  })();
                }}>
                  {t("cut-job.cancel", "Cancel the job")}
                </Button>
              )}
              {draft && (
                <Button variant="ghost" disabled={busy} onClick={() => {
                  void (async () => {
                    if (await ask(
                      t("cut-job.delete.title", "Delete draft {{n}}?", { n: job.number }),
                      t("cut-job.delete.message", "The draft and its documents go; its lots' panels are free again."),
                      t("button.delete", "Delete"),
                    )) remove.mutate();
                  })();
                }}>
                  {t("button.delete", "Delete")}
                </Button>
              )}
            </div>
          ) : null
        }
      />
      <div className="flex flex-col gap-5 overflow-auto p-4 lg:p-6">
        <JobHeader job={job} editable={canWrite && open} />

        {panels.length === 0 ? (
          <p className="rounded-md border border-dashed border-line px-4 py-6 text-center text-[13px] text-ink-muted">
            {t("cut-job.lots.none", "No lot yet: add the lots Cut Rite batched.")}
          </p>
        ) : (
          panels.map((group) => (
            <MaterialCard
              key={group.virtual_part_id}
              group={group}
              pieces={pieces.filter((piece) => piecePanels[String(piece.id)] === group.virtual_part_id)}
              onRemove={canWrite && draft
                ? (lot) => removePanel.mutate({ production_order_id: lot.production_order_id, virtual_part_id: group.virtual_part_id })
                : null}
              busy={busy}
            />
          ))
        )}

        <section>
          <h3 className="mb-2 text-[14px] font-semibold">{t("cut-job.documents", "Documents")}</h3>
          <CutJobDocuments documents={documents} owner={open ? { cutJobId: job.id } : null}
            defaultKind={job.process_code === "FOAM" ? "CUTRITE_FOAM" : "CUTRITE_CUT"} />
        </section>
      </div>
      {adding && <AddLotsDialog jobId={jobId} processCode={job.process_code ?? "CUT"} onClose={() => setAdding(false)} onAdded={settle} />}
    </>
  );
}

/**
 * One material of the job: its raw panels in total (planned, used, scrapped),
 * then its lots, one row each: the lot, its product, what it plans of this
 * material and what was used of it, its pieces cut. A row opens on its pieces.
 */
function MaterialCard({
  group,
  pieces,
  onRemove,
  busy,
}: {
  group: CutJobPanelGroupDTO;
  pieces: ProductionOrderOperationDTO[];
  onRemove: ((lot: CutJobGroupLotDTO) => void) | null;
  busy: boolean;
}) {
  const { t } = useTranslation();
  // Every lot open: the page shows all its pieces; a lot can be folded.
  const [folded, setFolded] = useState<Set<number>>(new Set());
  const used = group.lines.reduce((sum, line) => sum + Number(line.used), 0);
  const scrapped = group.lines.reduce((sum, line) => sum + Number(line.scrapped), 0);
  return (
    <section className="rounded-md border border-line">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line bg-surface-sunk px-3 py-2">
        <h3 className="text-[14px] font-semibold">{group.virtual_name}</h3>
        <span className="font-mono text-[12px] text-ink-muted">{group.virtual_number}</span>
        <span className="ml-auto text-[13px] tabular-nums">
          {t("cut-job.material.totals", "{{planned}} · used {{used}}", { planned: panelsText(group.lines), used: formatNumber(used) })}
          {scrapped > 0 && <span className="ml-2 text-warn">{t("cut-job.material.scrapped", "{{n}} scrapped", { n: formatNumber(scrapped) })}</span>}
        </span>
      </header>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-ink-muted">
            <th className="w-8" />
            <th className="px-2 py-1.5 font-medium">{t("cut-job.lot", "Lot")}</th>
            <th className="px-2 py-1.5 font-medium">{t("cut-job.product", "Product")}</th>
            <th className="px-2 py-1.5 font-medium">{t("cut-job.panels-planned", "Panels planned")}</th>
            <th className="px-2 py-1.5 text-right font-medium">{t("cut-job.used", "Used")}</th>
            <th className="px-2 py-1.5 text-right font-medium">{t("cut-job.pieces-cut", "Pieces cut")}</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {group.lots.map((lot) => {
            const expanded = !folded.has(lot.production_order_id);
            return (
              <LotRows
                key={lot.production_order_id}
                lot={lot}
                pieces={pieces.filter((piece) => piece.production_order_id === lot.production_order_id)}
                expanded={expanded}
                onToggle={() =>
                  setFolded((current) => {
                    const next = new Set(current);
                    if (next.has(lot.production_order_id)) next.delete(lot.production_order_id);
                    else next.add(lot.production_order_id);
                    return next;
                  })
                }
                onRemove={onRemove ? () => onRemove(lot) : null}
                busy={busy}
              />
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function LotRows({
  lot,
  pieces,
  expanded,
  onToggle,
  onRemove,
  busy,
}: {
  lot: CutJobGroupLotDTO;
  pieces: ProductionOrderOperationDTO[];
  expanded: boolean;
  onToggle: () => void;
  onRemove: (() => void) | null;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const hasPieces = pieces.length > 0;
  const done = pieces.reduce((sum, piece) => sum + Number(piece.quantity_done ?? 0), 0);
  const required = pieces.reduce((sum, piece) => sum + Number(piece.quantity_required_counted ?? piece.quantity_required), 0);
  const charged = lot.panels.reduce((sum, panel) => sum + Number(panel.charged), 0);
  return (
    <>
      <tr className={cn("border-t border-line", hasPieces && "cursor-pointer hover:bg-surface-sunk")} onClick={hasPieces ? onToggle : undefined}>
        <td className="pl-2 text-ink-muted">
          {hasPieces && (expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />)}
        </td>
        <td className="px-2 py-1.5">
          <Link to="/production/orders/$orderId" params={{ orderId: String(lot.production_order_id) }} onClick={(event) => event.stopPropagation()}
            className="font-semibold tabular-nums text-accent hover:underline">
            {lot.number}
          </Link>
          {lot.due_date && <span className="ml-2 text-[12px] text-ink-muted">{lot.due_date}</span>}
        </td>
        <td className="px-2 py-1.5">
          <span className="font-mono">{lot.part_number}</span>
          <span className="ml-2 text-ink-muted">× {formatNumber(lot.quantity)}</span>
        </td>
        <td className="px-2 py-1.5 tabular-nums">{panelsText(lot.panels)}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">{charged > 0 ? formatNumber(charged) : ""}</td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          {hasPieces ? `${formatNumber(done)}/${formatNumber(required)}` : <span className="text-ink-muted">{t("cut-job.not-released", "not released")}</span>}
        </td>
        <td className="pr-2 text-right">
          {onRemove && (
            <button type="button" disabled={busy} aria-label={t("cut-job.remove-panel", "Take it out of the job")} title={t("cut-job.remove-panel", "Take it out of the job")}
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
              className="rounded p-1 text-ink-muted hover:bg-surface-sunk hover:text-risk">
              <X className="size-4" />
            </button>
          )}
        </td>
      </tr>
      {expanded &&
        hasPieces &&
        [...pieces]
          .sort((a, b) => (a.letter ?? "").localeCompare(b.letter ?? "", undefined, { numeric: true }) || (a.part_number ?? "").localeCompare(b.part_number ?? ""))
          .map((piece) => {
            const cut = Number(piece.quantity_done ?? 0);
            const needed = Number(piece.quantity_required_counted ?? piece.quantity_required);
            const scrap = Number(piece.quantity_scrap ?? 0);
            return (
              <tr key={piece.id} className="bg-surface-sunk/40 text-[12.5px]">
                <td />
                <td className="py-1 pl-6 pr-2">
                  <span className="inline-block min-w-8 text-[14px] font-bold">{piece.letter ?? ""}</span>
                </td>
                <td className="px-2 py-1" colSpan={2}>
                  <span className="font-mono">{piece.part_number}</span>
                  {piece.part_name && <span className="ml-2 text-ink-muted">{piece.part_name}</span>}
                </td>
                <td className="px-2 py-1 text-right">
                  {scrap > 0 && <Badge size="sm" variant="warning">{t("cut-job.scrap-n", "{{n}} scrap", { n: formatNumber(scrap) })}</Badge>}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  <span className={cut >= needed ? "font-semibold text-ok" : ""}>{formatNumber(cut)}</span>
                  <span className="text-ink-muted">/{formatNumber(needed)}</span>
                </td>
                <td className="pr-2 text-right">
                  <Badge size="sm" variant={piece.status === "DONE" ? "success" : piece.status === "ACTIVE" ? "info" : "neutral"}>
                    {piece.status === "DONE"
                      ? t("cut-job.piece.done", "cut")
                      : piece.status === "ACTIVE"
                        ? t("cut-job.piece.started", "started")
                        : piece.status === "SKIPPED"
                          ? t("cut-job.piece.skipped", "skipped")
                          : t("cut-job.piece.to-cut", "to cut")}
                  </Badge>
                </td>
              </tr>
            );
          })}
    </>
  );
}

/** What the office says on a job: Cut Rite's number, and a note for the saw. When it is due is its lots'. */
function JobHeader({ job, editable }: { job: CutJobDTO; editable: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const [cutrite, setCutrite] = useState(job.cutrite_number ?? "");
  const [note, setNote] = useState(job.note ?? "");
  useEffect(() => {
    setCutrite(job.cutrite_number ?? "");
    setNote(job.note ?? "");
  }, [job]);
  const dirty = cutrite !== (job.cutrite_number ?? "") || note !== (job.note ?? "");
  const save = useMutation({
    mutationFn: () => client.update({ ...job, cutrite_number: cutrite.trim() || null, note: note.trim() || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["production"] }),
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <section className="flex flex-wrap items-start gap-x-6 gap-y-3">
      <div className="flex items-center gap-2 pt-1.5">
        <Badge variant={cutJobStatusVariant(job.status)}>{cutJobStatusLabel(job.status, t)}</Badge>
        {job.due_date && <span className="text-[13px] text-ink-muted">{t("cut-job.due-on", "Due {{date}}", { date: job.due_date })}</span>}
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <span className="text-ink-muted">{t("cut-job.cutrite", "Cut Rite number")}</span>
        <Input className="w-40" value={cutrite} disabled={!editable} placeholder={t("cut-job.optional", "Optional")}
          onChange={(event) => setCutrite(event.target.value)} />
      </label>
      <label className="flex min-w-72 flex-1 items-start gap-2 text-[13px]">
        <span className="pt-2 text-ink-muted">{t("cut-job.note", "Note for the saw")}</span>
        <Textarea className="flex-1" rows={1} value={note} disabled={!editable} onChange={(event) => setNote(event.target.value)} />
      </label>
      {editable && dirty && (
        <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          {t("button.save", "Save")}
        </Button>
      )}
    </section>
  );
}

function AddLotsDialog({ jobId, processCode, onClose, onAdded }: { jobId: number; processCode: string; onClose: () => void; onAdded: (next: CutJobDetailDTO) => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const [selected, setSelected] = useState<PanelRef[]>([]);
  const add = useMutation({
    mutationFn: () => client.addPanels(jobId, selected),
    onSuccess: (next) => {
      onAdded(next);
      onClose();
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      size="xl"
      title={t("cut-job.add-lots", "Add lots")}
      description={t("cut-job.add-lots.hint", "Open production orders with a confirmed production BOM, one row per material not in a job yet.")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>{t("dialog.cancel", "Cancel")}</Button>
          <Button variant="primary" loading={add.isPending} disabled={selected.length === 0} onClick={() => add.mutate()}>
            {t("cut-job.add-selected", "Add {{n}}", { n: selected.length })}
          </Button>
        </>
      }
    >
      <div className="flex h-[60vh] min-h-0 flex-col">
        <CandidateTable processCode={processCode} onSelect={setSelected} />
      </div>
    </Dialog>
  );
}
